import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import '../styles/Projects.css';

// Import experience images
import honda from '../assets/images/experience/honda.JPG';
import hackthe6ix from '../assets/images/experience/hackthe6ix.JPG';
import blueprint from '../assets/images/experience/blueprint.png';
import deca from '../assets/images/experience/deca.JPG';
import wics from '../assets/images/experience/wics3.png';
import bhklab from '../assets/images/experience/bhk.png';
import summit from '../assets/images/experience/summit.jpg';
import datadog from '../assets/images/datadog.jpg';
import uhn from '../assets/images/experience/uhn.png';

const ExperienceModal = ({ experience, onClose }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        <h2 className="modal-title">{experience.title}</h2>
        <p className="modal-details">{experience.description}</p>
        <img src={experience.image} alt={experience.title} className="modal-image" />
        <p className="modal-details">{experience.longDescription}</p>
        {experience.website && (
          <a 
            href={experience.website}
            className="website-button"
            target="_blank"
            rel="noopener noreferrer"
          >
            website
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              className="arrow-icon"
            >
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </a>
        )}
      </div>
    </div>
  );
};

const experiences = [
  {
    id: 'datadog',
    title: 'DATADOG',
    description: 'database optimization & query performance tooling',
    image: datadog,
    longDescription: 'software engineering intern at datadog on the database monitoring team. built a go matching engine to trace applied query rewrites back to their original queries, and a recommendation workflow template that ranked unapplied postgresql rewrites by production workload metrics to increase optimization adoption.',
    website: 'https://www.datadoghq.com/'
  },
  {
    id: 'wics',
    title: 'WOMEN IN COMPUTER SCIENCE',
    description: 'championing women in tech',
    image: wics,
    longDescription: 'co-chair of women in computer science club, leading academic, career, and community events for 100+ women in cs!',
    website: 'https://wics.uwaterloo.ca/'
  },
  {
    id: 'shopify',
    title: 'SHOPIFY',
    description: 'software engineering & large-scale systems',
    image: summit,
    longDescription: 'software engineering intern at shopify, working on backend systems for the ads engine team. built ruby services that improved campaign reliability, performance, and deployment across google ads integrations.',
    website: 'https://www.shopify.com/ca'
  },
  {
    id: 'bhklab',
    title: 'BHK LAB',
    description: 'pharmacogenomics research',
    image: bhklab,
    longDescription: 'undergraduate research assistant at the bhk lab under dr. benjamin haibe-kains, working on xevadb and building data pipelines to transform large-scale drug screening data into cloud-hosted pharmacogenomic resources.',
    website: 'https://bhklab.ca/'
  },
  {
    id: 'blueprint',
    title: 'UW BLUEPRINT',
    description: 'leukemia & lymphoma society of canada',
    image: blueprint,
    longDescription: 'engineering for the leukemia & lymphoma society of canada first connection peer matching and scheduling platform, amplifying social sector impact through tech.',
    website: "https://uwblueprint.org/"
  },
  {
    id: 'uhn',
    title: 'UNIVERSITY HEALTH NETWORK',
    description: 'web development & internal tooling',
    image: uhn,
    longDescription: 'developed a searchable staff directory for 20,000+ employees using next.js and typescript, integrated with dotcms via 33 modular ui components, and built a researcher keyword filter system with lazy-loaded rest apis.',
    website: 'https://uhn.ca'
  },
  {
    id: 'honda',
    title: 'HONDA CANADA',
    description: 'salesforce development & api integration',
    image: honda,
    longDescription: 'implemented a customer portal on salesforce experience cloud, optimized system efficiency by refactoring 87 workflow rules, and resolved product validation issues by redesigning a rest api.',
    website: "https://www.honda.ca/en/"
  },
  {
    id: 'hackthe6ix',
    title: 'HACK THE 6IX',
    description: 'organizing toronto\'s largest summer hackathon',
    image: hackthe6ix,
    longDescription: 'managing logistics for 400+ participants, including venue, merchandise, awards, and catering, while securing $15,000 in sponsor prizes.',
    website: "https://hackthe6ix.com/"
  },
  {
    id: 'deca',
    title: 'ONTARIO DECA OFFICER',
    description: 'media, events, & tech for competitions',
    image: deca,
    longDescription: 'developed the ontario deca website (wordpress) and mobile app (glide), hosted 8 conferences for 15,000+ students, and designed 250+ multimedia works for 10,000+ users, including the provincial program.',
    website: "https://deca.ca/"
  },
  // {
  //   id: 'uptoronto',
  //   title: '1UP TORONTO',
  //   description: 'transforming toronto\'s public spaces',
  //   image: uptoronto,
  //   longDescription: 'president of 1UP toronto, a youth-led program empowering the next generation of city builders, led a 10+ member team, organized events like the 1UP leader\'s lab and design competition, and supported youth-led projects to improve public spaces across the gta.',
  //   website: "https://1upyouth.org/"
  // },
  // {
  //   id: 'sac',
  //   title: 'STUDENT COUNCIL',
  //   description: 'events, programs & leadership for student body',
  //   image: sac,
  //   longDescription: 'elected youngest student council president at toronto\'s 2nd largest high school, led a 125+ member team, organized events for over 400 students (e.g., grade orientation, semi-formal) and launched initiatives like a mentorship program and house system rebrand.',
  //   website: "https://sites.google.com/tdsb.on.ca/mgcisac/home"
  // },
];

// Preload images for better caching
const preloadImage = (src) => {
  const img = new Image();
  img.src = src;
};

const Experience = () => {
  const [isDark, setIsDark] = useState(() => {
    // On mobile, default to light mode
    const isMobile = window.innerWidth <= 640;
    if (isMobile) {
      return false;
    }
    // Read from localStorage on initial load for desktop
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });
  const [selectedExperience, setSelectedExperience] = useState(null);

  // Preload all experience images on component mount
  useEffect(() => {
    experiences.forEach(experience => {
      preloadImage(experience.image);
    });
  }, []);

  useEffect(() => {
    // Save to localStorage whenever dark mode changes
    localStorage.setItem('darkMode', isDark.toString());
    
    if (isDark) {
      document.body.style.backgroundColor = '#111827';
      document.documentElement.style.backgroundColor = '#111827';
    } else {
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, [isDark]);

  return (
    <>
      <div className={`container ${isDark ? 'dark' : ''}`}>
        <header className="header">
          <nav className="nav">
            <Link to="/" className="nav-link">home</Link>
            <Link to="/experience" className="nav-link">experience</Link>
            <Link to="/projects" className="nav-link">projects</Link>
            <a href="/design" className="nav-link">design</a>
            <button 
              onClick={() => setIsDark(!isDark)}
              className="theme-toggle"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </nav>
        </header>

        <section className="projects-content">
        <br></br>
          <h1 className="page-title">EXPERIENCE</h1>
          <p className="page-description">
          passionate about community development (1300+ volunteer hours)
        </p>
        <p className="page-description">
        i find meaning in working on things that help people and bring ideas to life, whether it be through engineering, design, or community development!
        </p>

          <br />
          <br />
          <div className="projects-grid">
            {experiences.map((experience, index) => (
              <div 
                key={experience.id} 
                className="project-card"
                onClick={() => setSelectedExperience(experience)}
              >
                <img 
                  src={experience.image} 
                  alt={`${experience.title}`} 
                  className="project-image"
                  loading={index < 3 ? "eager" : "lazy"}
                  fetchPriority={index < 3 ? "high" : "auto"}
                  decoding="async"
                />
                <h2 className="project-title">{experience.title}</h2>
                <p className="project-description">{experience.description}</p>
              </div>
            ))}
          </div>
        </section>

        {selectedExperience && (
          <ExperienceModal 
            experience={selectedExperience} 
            onClose={() => setSelectedExperience(null)} 
          />
        )}

        <footer className="footer">
          <p>made with <span className="highlight">♥</span> in <span className="highlight">toronto</span></p>
        </footer>
        <br></br>
      </div>
    </>
  );
};

export default Experience; 